import { StopAddressType, StopTimeType, StopType } from "types/MultiStop";
import type { ScheduleStopValue } from "@RequestFormV2/defaults/scheduleDefaults";

function mapAddressTypeToApi(value: StopAddressType | undefined): string {
  switch (value) {
    case StopAddressType.AIRPORT:
      return "airport";
    case StopAddressType.SEAPORT:
      return "seaport";
    default:
      return "address";
  }
}

function mapPickupCargoReady(stopTimeType: StopTimeType | undefined): string | undefined {
  switch (stopTimeType) {
    case StopTimeType.AFTER:
      return "after";
    case StopTimeType.BETWEEN:
      return "between";
    case StopTimeType.ASAP_DELIVERY:
      return "asap_delivery";
    default:
      return undefined;
  }
}

function mapDeliveryCargoReady(stopTimeType: StopTimeType | undefined): string | undefined {
  switch (stopTimeType) {
    case StopTimeType.BEFORE:
      return "before";
    case StopTimeType.BETWEEN:
      return "between";
    case StopTimeType.ASAP_DELIVERY:
      return "asap_delivery";
    default:
      return undefined;
  }
}

function omitEmptyValues(obj: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined && value !== null && value !== ""),
  );
}

function mapStopToPickupFields(stop: ScheduleStopValue): Record<string, unknown> {
  return omitEmptyValues({
    pickup_address_type: mapAddressTypeToApi(stop.stop_address_type),
    pickup_ready_date: stop.stop_after_date,
    pickup_ready_time: stop.stop_after_time,
    pickup_before_date: stop.stop_before_date,
    pickup_before_time: stop.stop_before_time,
    pickup_timezone: stop.stop_timezone,
    pickup_cargo_ready: mapPickupCargoReady(stop.stop_time_type),
    pickup_contact_name: stop.stop_contact_name,
    pickup_contact_phone: stop.stop_contact_number,
    pickup_contact_email: stop.stop_contact_email,
    pickup_notes: stop.stop_notes,
    pickup_hours_from: stop.stop_hours_start,
    pickup_hours_to: stop.stop_hours_end,
    pickup_street: stop.street_address,
    pickup_street_line2: stop.street_address_line_two,
    pickup_address_name: stop.address_name,
    pickup_company_name: stop.address_name,
    pickup_city: stop.city_name,
    pickup_state: stop.state_name,
    pickup_country: stop.country_name,
    pickup_zipcode: stop.zipcode,
    pickup_address_pk: stop.address_id,
    pickup_address_reference_number: stop.address_reference_number,
    pickup_airport_code: stop.stop_airport_code,
    pickup_airport: stop.stop_airport_name,
    pickup_seaport_code: stop.stop_seaport_code,
    pickup_seaport: stop.stop_seaport_name,
    pickup_location_reference_number: stop.location_reference_number,
    pickup_custom_address_reference: stop.custom_address_reference,
  });
}

function pickFirstDefined<T>(...values: (T | null | undefined)[]): T | undefined {
  return values.find((value) => value !== undefined && value !== null && value !== "") as T | undefined;
}

function mapStopToDeliveryFields(stop: ScheduleStopValue): Record<string, unknown> {
  // Classic quick-request API validates stop1_after_* even when RFV2 "Before" UI
  // (and service-level auto-fill) store the primary datetime on stop_before_*.
  const stop1AfterDate = pickFirstDefined(stop.stop_after_date, stop.stop_before_date);
  const stop1AfterTime = pickFirstDefined(stop.stop_after_time, stop.stop_before_time);

  return omitEmptyValues({
    stop1_address_type: mapAddressTypeToApi(stop.stop_address_type),
    stop1_after_date: stop1AfterDate,
    stop1_after_time: stop1AfterTime,
    stop1_before_date: stop.stop_before_date,
    stop1_before_time: stop.stop_before_time,
    stop1_timezone: stop.stop_timezone,
    delivery_cargo_ready: mapDeliveryCargoReady(stop.stop_time_type),
    stop1_contact_name: stop.stop_contact_name,
    stop1_contact_phone: stop.stop_contact_number,
    stop1_contact_email: stop.stop_contact_email,
    stop1_notes: stop.stop_notes,
    delivery_hours_from: stop.stop_hours_start,
    delivery_hours_to: stop.stop_hours_end,
    stop1_street: stop.street_address,
    stop1_street_line2: stop.street_address_line_two,
    stop1_address_name: stop.address_name,
    stop1_company_name: stop.address_name,
    stop1_city: stop.city_name,
    stop1_state: stop.state_name,
    stop1_country: stop.country_name,
    stop1_zipcode: stop.zipcode,
    destination_address_pk: stop.address_id,
    destination_address_reference_number: stop.address_reference_number,
    stop1_airport_code: stop.stop_airport_code,
    stop1_airport: stop.stop_airport_name,
    stop1_seaport_code: stop.stop_seaport_code,
    stop1_seaport: stop.stop_seaport_name,
    stop1_location_reference_number: stop.location_reference_number,
    stop1_custom_address_reference: stop.custom_address_reference,
  });
}

function getBoundaryStops(stops: ScheduleStopValue[]): {
  pickup?: ScheduleStopValue;
  delivery?: ScheduleStopValue;
} {
  const pickupByType = stops.find((stop) => stop.stop_type === StopType.PICKUP);
  const deliveryByType = [...stops].reverse().find((stop) => stop.stop_type === StopType.DROPOFF);

  // A lone DROPOFF must not fall back to pickup_* — stripUnselectedScheduleAddresses
  // can leave only delivery after a partial autosave.
  const pickup = pickupByType ?? (deliveryByType ? undefined : stops[0]);
  const delivery = deliveryByType ?? (pickupByType && stops.length > 1 ? stops.at(-1) : undefined);

  if (pickup && delivery && pickup === delivery) {
    return { pickup, delivery: undefined };
  }

  return { pickup, delivery };
}

/** Form indexes of the stops that `buildClassicScheduleFromStops` sends as pickup_* and stop1_*. */
export function getClassicScheduleStopIndexes(stops: ScheduleStopValue[]): { pickup?: number; delivery?: number } {
  const { pickup, delivery } = getBoundaryStops(stops);
  return {
    pickup: pickup ? stops.indexOf(pickup) : undefined,
    delivery: delivery ? stops.indexOf(delivery) : undefined,
  };
}

/**
 * Quick Request persists classic flat pickup_* / stop1_* schedule fields, not stops[].
 * Map the RFV2 stop cards back to that shape before draft save or convert.
 */
export function buildClassicScheduleFromStops(schedule: Record<string, unknown>): Record<string, unknown> {
  const { stops } = schedule;
  if (!Array.isArray(stops) || stops.length === 0) {
    const { stops: _stops, multistop_instructions: _instructions, ...rest } = schedule;
    return rest;
  }

  const { pickup, delivery } = getBoundaryStops(stops as ScheduleStopValue[]);
  const { stops: _stops, multistop_instructions: _instructions, ...rest } = schedule;

  return {
    ...rest,
    ...(pickup ? mapStopToPickupFields(pickup) : {}),
    ...(delivery ? mapStopToDeliveryFields(delivery) : {}),
  };
}
